/**
 * Which links a storefront can actually reach.
 *
 * Template footers ship curated menus — "Our Story", "FAQ", "Shipping &
 * Returns" — pointing at `/about`, `/faq` and friends. Only `/contact` is a
 * real route; the rest are dynamic-page slugs that 404 unless the merchant has
 * published a page with exactly that slug. Most haven't, so those menus were
 * mostly dead links, and Atelier's footer alone offered `/about` four times.
 *
 * The published-page list arrives with the storefront config, which loads
 * through one function, so it is recorded here once and read wherever a link
 * is rendered rather than threaded through 58 call sites.
 */

/** Routes the SPA always serves, regardless of what the merchant published. */
const ALWAYS_ROUTABLE = new Set([
  '/',
  '/products',
  '/cart',
  '/checkout',
  '/contact',
  '/orders',
  '/my-bookings',
]);

let knownSlugs: Set<string> | null = null;

/** Called when a storefront config lands. An empty list is still an answer. */
export function setKnownPageSlugs(slugs: Array<string | undefined | null>): void {
  knownSlugs = new Set(
    (slugs || [])
      .map((s) => String(s || '').trim().toLowerCase().replace(/^\/+/, ''))
      .filter(Boolean),
  );
}

/**
 * Can this link go somewhere? Unknown-so-far means yes: before the config
 * lands we must not blank out a footer we are about to be able to render.
 */
export function isResolvableLink(url: string | undefined | null): boolean {
  const href = String(url || '').trim();
  if (!href) return false;

  // A bare "#" is a placeholder, not a destination — Sugar's FOLLOW column
  // shipped three of them and they just reloaded the page. A "#section"
  // anchor is real navigation on single-page templates, so that stays.
  if (href === '#') return false;

  // External, anchors, mail/tel — not ours to judge.
  if (/^(https?:|mailto:|tel:|#)/i.test(href)) return true;
  if (!href.startsWith('/')) return true;

  const path = href.toLowerCase().split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (ALWAYS_ROUTABLE.has(path)) return true;
  // /products/:slug, /orders/:id and similar nested app routes.
  if (path.split('/').length > 2) return true;

  if (knownSlugs === null) return true;
  return knownSlugs.has(path.replace(/^\/+/, ''));
}
